import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SearchablePicker } from '@/components/ui/SearchablePicker';
import { useAgents } from '@/hooks/data/useAgents';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the chosen makler profile id when the user confirms. */
  onSelect: (profileId: string) => void;
  /** The primary makler — excluded from the list (backend would reject
   *  with 400 already_primary anyway). */
  excludeProfileId?: string | null;
  busy?: boolean;
}

// Seller-rules batch — picker for the SECOND makler on a seller.
// Active MAKLERS only (no CC/admin — backend rejects 400 invalid_assignee).
export function AssignSecondaryAgentDialog({
  open,
  onOpenChange,
  onSelect,
  excludeProfileId,
  busy,
}: Props) {
  const { t } = useTranslation();
  const { data: agents = [] } = useAgents();
  const [target, setTarget] = useState('');

  const options = agents
    .filter(
      (a) =>
        a.status === 'active' && a.role === 'makler' && a.id !== excludeProfileId,
    )
    .map((a) => ({
      value: a.id,
      label: a.full_name,
      secondary: a.agent_id,
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('clients.secondary.title', 'Zweiten Makler zuweisen')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-sm text-ink-3">
            {t(
              'clients.secondary.hint',
              'Der zweite Makler erhält volle Rechte auf diesen Verkäufer (Bearbeiten, Termine, „Meine"-Ansicht, Telefonnummer). Der zuständige Makler bleibt unverändert.',
            )}
          </p>
          <SearchablePicker
            value={target}
            onChange={setTarget}
            placeholder={t('clients.secondary.pick', 'Makler suchen …')}
            emptyText={t('common.no_matches', 'Keine Treffer')}
            options={options}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Abbrechen')}
          </Button>
          <Button
            type="button"
            disabled={!target || busy}
            onClick={() => target && onSelect(target)}
          >
            {busy
              ? t('clients.secondary.assigning', 'Wird zugewiesen …')
              : t('clients.secondary.assign', 'Zuweisen')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
